import React, { useState } from 'react';
import { X } from 'lucide-react';
import { TrainingCourse } from '../types';

interface Props {
  courses: TrainingCourse[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

export const AssignmentCreateModal: React.FC<Props> = ({
  courses,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [personName, setPersonName] = useState('Captain Burak Demir');
  const [personId, setPersonId] = useState('USR-FO-01');
  const [personDepartment, setPersonDepartment] = useState('Flight Operations');
  const [courseId, setCourseId] = useState(courses[0]?.id || '');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>('MEDIUM');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        person_id: personId,
        person_name: personName,
        person_department: personDepartment,
        course_id: courseId,
        due_at: dueDate ? new Date(dueDate).toISOString() : undefined,
        priority,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating training assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-xl">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Assign Training to Personnel</h3>
            <p>Mandate course completion and track regulatory qualification compliance</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-grid-2">
              <div className="form-field">
                <label>Personnel *</label>
                <select
                  value={personId}
                  onChange={(e) => {
                    setPersonId(e.target.value);
                    if (e.target.value === 'USR-FO-01') {
                      setPersonName('Captain Burak Demir');
                      setPersonDepartment('Flight Operations');
                    } else if (e.target.value === 'USR-QA-01') {
                      setPersonName('Selin Ozturk');
                      setPersonDepartment('Safety & Quality');
                    } else {
                      setPersonName('Zeynep Kaya');
                      setPersonDepartment('Ground Operations');
                    }
                  }}
                >
                  <option value="USR-FO-01">Captain Burak Demir (Flight Ops)</option>
                  <option value="USR-QA-01">Selin Ozturk (Quality & Safety)</option>
                  <option value="USR-SEC-01">Zeynep Kaya (Ground Ops)</option>
                </select>
              </div>

              <div className="form-field">
                <label>Target Course *</label>
                <select
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                >
                  {courses.map((crs) => (
                    <option key={crs.id} value={crs.id}>
                      {crs.course_code} - {crs.title.slice(0, 30)}...
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Completion Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Priority</label>
                <select
                  value={priority}
                  onChange={(e: any) => setPriority(e.target.value)}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High (Mandatory)</option>
                  <option value="URGENT">Urgent (Overdue Risk)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Assigning...' : 'Assign Training'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
